import { useState } from 'react';
import { TrashIcon, PlusIcon, ListIcon } from '@phosphor-icons/react';
import useSWR from 'swr';
import toast from 'react-hot-toast';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';

import { invariant } from '@utils/invariant';
import { getDisplayError } from '@utils/get-display-error';
import { ITableHeader, Table } from 'components/table/Table';
import { LinkButton } from 'components/button/ButtonLink';
import { ConfirmDialog } from '../../../../components/dialog/ConfirmDialog';
import { FormDialog } from '../../../../components/dialog/FormDialog';
import { InputField } from 'components/input/InputField';
import { CheckboxField } from 'components/checkbox/CheckboxField';
import { SpinnerBlock } from '../../../../components/Spinner';
import { EmptyState } from 'components/empty-state/EmptyState';
import { captureException } from '@sentry/react';
import { ErrorState } from 'components/error-state/ErrorState';
import { fetchAdminApiKeys, createAdminApiKey, revokeAdminApiKey } from './customer-api-key-models';
import { API_KEY_SCOPES } from 'src/app/customer-api/utils/api-key-scopes';
import { getKeyStatus } from 'src/app/customer-api/utils/get-key-status';
import { ApiKeyStatusPill } from 'src/app/customer-api/components/ApiKeyStatusPill';
import { ApiKeyScopePill } from 'src/app/customer-api/components/ApiKeyScopePill';
import { CreatedKeyDialog } from 'src/app/customer-api/components/CreatedKeyDialog';

const STATUS_LABELS = {
  active: 'Actief',
  revoked: 'Ingetrokken',
  expired: 'Verlopen',
} as const;

const SCOPE_LABELS: Record<string, string> = {
  [API_KEY_SCOPES.ORDERS_READ]: 'Orders lezen',
  [API_KEY_SCOPES.ORDERS_WRITE]: 'Orders aanmaken',
};

const CREATED_KEY_LABELS = {
  title: 'API sleutel aangemaakt',
  warning: 'Bewaar deze sleutel veilig. Deze wordt niet meer getoond.',
  copyButton: 'Kopieer sleutel',
  copiedToast: 'Sleutel gekopieerd',
} as const;

const createApiKeySchema = Yup.object().shape({
  name: Yup.string().min(1, 'Naam is verplicht').required('Naam is verplicht'),
  expiresAt: Yup.string(),
});

export const CustomerApiKeysTab = () => {
  const { customerId: customerIdParam } = useParams<{ customerId: string }>();
  invariant(customerIdParam);
  const customerId = +customerIdParam;
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  const {
    data: apiKeys,
    error,
    isLoading,
    mutate,
  } = useSWR(`admin-api-keys-${customerId}`, () => fetchAdminApiKeys(customerId));

  const tableHeaders: ITableHeader[] = [
    { id: 'name', name: 'Naam' },
    { id: 'prefix', name: 'Prefix' },
    { id: 'scopes', name: 'Rechten' },
    { id: 'status', name: 'Status' },
    { id: 'lastUsed', name: 'Laatst gebruikt' },
    { id: 'createdAt', name: 'Aangemaakt' },
    { id: 'expiresAt', name: 'Verloopt' },
    { id: 'actions', name: '' },
  ];

  if (isLoading) {
    return <SpinnerBlock message="API sleutels laden..." />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={() => mutate()} variant="section" />;
  }

  return (
    <div>
      <div className="flex justify-end gap-2 mb-4">
        <LinkButton to="logs" color="secondary" iconLeft={<ListIcon className="button-icon" />}>
          API Logs
        </LinkButton>
        <FormDialog
          triggerText={
            <span className="flex items-center gap-1">
              <PlusIcon className="button-icon" />
              API Sleutel aanmaken
            </span>
          }
          triggerColor="primary"
          title="API Sleutel aanmaken"
          submitText="Aanmaken"
          validationSchema={createApiKeySchema}
          initialValues={{
            name: '',
            ordersRead: true,
            ordersWrite: false,
            expiresAt: '',
          }}
          onSubmit={async (values) => {
            const scopes: string[] = [];
            if (values.ordersRead) scopes.push(API_KEY_SCOPES.ORDERS_READ);
            if (values.ordersWrite) scopes.push(API_KEY_SCOPES.ORDERS_WRITE);

            if (scopes.length === 0) {
              throw new Error('Minstens één recht is verplicht');
            }

            try {
              const result = await createAdminApiKey({
                customerId,
                name: values.name,
                scopes,
                expiresAt: values.expiresAt ? new Date(values.expiresAt).toISOString() : undefined,
              });
              setCreatedKey(result.fullKey);
              mutate();
            } catch (err) {
              captureException(err);
              toast.error('Kon API sleutel niet aanmaken: ' + getDisplayError(err));
              throw err;
            }
          }}
        >
          <div className="mb-4">
            <InputField labelText="Naam" name="name" type="text" />
          </div>
          <div className="mb-4">
            <div className="text-sm font-medium mb-2">Rechten</div>
            <div className="flex flex-col gap-2">
              <CheckboxField labelText="Orders lezen" name="ordersRead" />
              <CheckboxField labelText="Orders aanmaken" name="ordersWrite" />
            </div>
          </div>
          <div className="mb-4">
            <InputField labelText="Verloopt op (optioneel)" name="expiresAt" type="date" />
          </div>
        </FormDialog>
      </div>

      {!apiKeys || apiKeys.length === 0 ? (
        <EmptyState title="Geen API sleutels gevonden" />
      ) : (
        <Table
          idKey="id"
          headers={tableHeaders}
          data={apiKeys}
          mapData={(key) => {
            const status = getKeyStatus(key);
            return [
              key.name,
              <code className="text-xs" key="prefix">
                td_customer_{key.keyPrefix}...
              </code>,
              <div className="flex gap-1" key="scopes">
                {key.scopes.map((s) => (
                  <ApiKeyScopePill key={s} label={SCOPE_LABELS[s] ?? s} />
                ))}
              </div>,
              <ApiKeyStatusPill key="status" apiKey={key} labels={STATUS_LABELS} />,
              key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleDateString() : 'Nooit',
              new Date(key.createdAt).toLocaleDateString(),
              key.expiresAt ? new Date(key.expiresAt).toLocaleDateString() : '-',
              status === 'active' ? (
                <ConfirmDialog
                  triggerColor="danger"
                  triggerText={<TrashIcon className="button-icon" />}
                  title="API Sleutel intrekken"
                  submitText="Intrekken"
                  description={`Weet je zeker dat je API sleutel "${key.name}" wilt intrekken? Dit kan niet ongedaan worden gemaakt.`}
                  onSubmit={async () => {
                    try {
                      await revokeAdminApiKey(key.id);
                      toast.success('API sleutel is ingetrokken');
                      mutate();
                    } catch (err) {
                      captureException(err);
                      toast.error('Kon API sleutel niet intrekken: ' + getDisplayError(err));
                    }
                  }}
                />
              ) : null,
            ];
          }}
        />
      )}

      {createdKey && (
        <CreatedKeyDialog
          fullKey={createdKey}
          open={!!createdKey}
          onOpenChange={() => setCreatedKey(null)}
          labels={CREATED_KEY_LABELS}
        />
      )}
    </div>
  );
};
